import {AbstractParameter} from 'parameters/AbstractParameter.js';
import {EFFECT} from 'constants/object-types.js';
import {bulkUpdate} from 'utils/bulkUpdate.js';
import {saveParameterToJson, loadParameterFromJson} from 'parameters/parameterJsonFunctions.js';
import {uuid} from 'uuidv4';

/**
 * Midi effects like "Arp" should be sub classes of this
 * in theory, should be able to link a series of midi effects
 * and have that effect the ultimate set of notes that are 
 * triggered by the sequencer
 */

class AbstractEffect {
    constructor(name, track) {
        this.id = uuid();
        this.track = track;
        this.sequencerEngine = track.sequencerEngine;
        this.store = this.sequencerEngine.store;
        this.name = name;
        this.parameters = [];
        this.enabledParameter = new AbstractParameter(
            'enabled', this, (o, x) => [],
            {
                min: false,
                max: true,
                unit: '',
                defaultValue: true,
            });

        this.registerParameter(this.enabledParameter);

        this.numberOfOutputs = 1;
    }
    
    getObjectType() {
        return EFFECT;
    }
    
    get enabled() {
        return this.enabledParameter.get();
    }

    set enabled(x) {
        this.enabledParameter.set(x);
    }

    toggleEnabled() {
        this.enabled = !this.enabled;
    }

    isEnabled() {
        return this.enabled;
    }

    getJson() {
        let json = {
            name: this.name,
            enabled: this.enabled,
            id: this.id,
            x: this.x,
            y: this.y,
        }

        json = this.saveParameterToJson(
            json, 'enabledParameter', this.enabledParameter);

        for (let i in this.parameters) {
            let parameter = this.parameters[i];
            json[parameter.name] = parameter.get();
            let fullParameterName = parameter.name + 'Parameter';
            json = this.saveParameterToJson(
                json, fullParameterName, parameter);
        }

        return json;
    }

    fromJson(json, isCopy=false) {
        if (json.id !== undefined && !isCopy) {
            this.id = json.id;
            if (this.track.sequencerEngine) {
                this.track.sequencerEngine.idToObject[this.id] = this;
            }
        }

        if (json.x !== undefined) {
            this.x = json.x;
            this.y = json.y;
        }

        let bulk = {};
        bulkUpdate(this.enabledParameter, json.enabled, bulk);

        bulk = {
            ... bulk,
            ... this.loadParameterFromJson(json, 'enabledParameter', this.enabledParameter)
        };

        for (let i in this.parameters) {
            let parameter = this.parameters[i];
            let value = json[parameter.name];
            if (value !== undefined && value !== null) {
                bulkUpdate(parameter, value, bulk);
            }
            bulk = {
                ... bulk,
                ... this.loadParameterFromJson(
                    json, parameter.name + 'Parameter', parameter, isCopy)
            };
        }
        return bulk;
    }

    /**
     * Registers the context of this effect, i.e. which track/group/send
     * it comes from
     */
    registerContext(context) {
        context = context.forEffect(this);
        this.parameters.forEach(
            parameter =>
                parameter.setContext(context));
    }

    registerMixSession(mixSession) {
        this.parameters.forEach(
            parameter =>
                parameter.setMixSession(mixSession));
    }

    registerTrack(track) {
        this.parameters.forEach(
            parameter =>
                parameter.setTrack(track));
    }
 
    registerParameter(parameter) {
        if (parameter !== undefined) {
            parameter.effectName = this.name;
            parameter.setStore(this.store);
            this.parameters.push(parameter);
        }
    }

    saveParameterToJson(json, name, parameter) {
        return saveParameterToJson(json, name, parameter);
    }

    loadParameterFromJson(json, fieldName, parameter, isCopy) {
        return loadParameterFromJson(json, fieldName, parameter, this.track.sequencerEngine, isCopy);
    }

    getEffectCopy() {
        return this.getCopy(this.track);
    }
}

export default AbstractEffect;
